import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Bootstrap silent-refresh timeout budget. `provideAppInitializer` blocks
 * router/guard evaluation until this settles (see design D2), so a hung or
 * slow `auth/token-refresh` call must not stall app boot indefinitely —
 * this is especially important for public-quoter's anonymous visitors, who
 * have no refresh-token cookie and must reach a rendered page fast.
 * 5s is generous for a same-region API round trip while still bounding the
 * worst case to something a user will tolerate before a blank screen.
 */
export const SILENT_REFRESH_TIMEOUT_MS = 5000;

/**
 * Used by `provideAppInitializer(() => silentRefresh(inject(AuthService)))`
 * in all 3 in-scope apps (agents, authentication, public-quoter).
 *
 * `AuthService.refreshToken()` already swallows its own HTTP failures
 * (catchError -> clearSession -> of(null)), so it never rejects on its own.
 * The `timeout()` + `catchError()` pair added here guards the one case it
 * does NOT cover: a request that never settles (hung backend, dropped
 * connection). On timeout we treat it exactly like "no session" — resolve,
 * do not reject, do not throw to the console — so bootstrap always
 * completes and the router/guards evaluate the (logged-out) state instead
 * of hanging on a blank screen.
 */
export function silentRefresh(
  authService: AuthService,
  timeoutMs: number = SILENT_REFRESH_TIMEOUT_MS
): Promise<void> {
  return firstValueFrom(
    authService.refreshToken().pipe(
      timeout(timeoutMs),
      catchError(() => of(null))
    )
  )
    .then(() => undefined)
    .catch(() => undefined);
}
